(() => {
  if (window.PDPInformationTabsVersion >= 1) return;
  window.PDPInformationTabsVersion = 1;

  const rootSelector = "[data-pdp-information-tabs]";
  const containerSelector = ".easytabs-container";
  const itemSelector = ".easytabs-content-item";
  const overviewPreviewLength = 200;
  const instances = new WeakMap();
  const containerIds = new WeakMap();
  let containerSequence = 0;
  let previewSequence = 0;

  function normalizeHandle(value) {
    return String(value || "")
      .replace(/^tabs-/, "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");
  }

  function setAttribute(element, name, value) {
    if (!element) return;
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  function setBooleanAttribute(element, name, enabled) {
    if (!element) return;
    if (enabled && element.getAttribute(name) !== "true") element.setAttribute(name, "true");
    if (!enabled && element.hasAttribute(name)) element.removeAttribute(name);
  }

  function overviewItem(container) {
    return Array.from(container.querySelectorAll(itemSelector)).find(item => {
      const handle = normalizeHandle(item.dataset.etHandle);
      if (handle === "overview") return true;
      const label = item.querySelector(".easytabs-accordion-item");
      return normalizeHandle(label?.textContent) === "overview";
    });
  }

  function correspondingTab(container, item) {
    const items = Array.from(container.querySelectorAll(itemSelector));
    const tabs = Array.from(container.querySelectorAll(".easytabs-tabs > .easytabs-tab-item"));
    const index = items.indexOf(item);
    if (index >= 0) return tabs[index] || null;
    const marker = item?.dataset.etMarker;
    return marker
      ? Array.from(tabs).find(tab => tab.dataset.etMarker === marker) || null
      : null;
  }

  function setTabActiveState(tab, active) {
    if (!tab) return;
    tab.classList.toggle("easytabs-active", active);
    tab.classList.remove("easytabs-before-active");
    tab.tabIndex = active ? 0 : -1;
    setAttribute(tab, "aria-selected", active ? "true" : "false");
  }

  function setAccordionActiveState(item, active) {
    if (!item) return;
    const accordion = item.querySelector(".easytabs-accordion-item");
    const content = item.querySelector(".easytabs-content-holder");
    item.classList.toggle("easytabs-active", active);
    item.classList.remove("easytabs-before-active");
    if (accordion) {
      setAttribute(accordion, "aria-expanded", active ? "true" : "false");
    }
    if (content) {
      setAttribute(content, "aria-hidden", active ? "false" : "true");
      content.inert = !active;
    }
  }

  function activateOnly(container, target, includeTabList) {
    container.querySelectorAll(itemSelector).forEach(item => setAccordionActiveState(item, item === target));
    if (includeTabList) {
      container.querySelectorAll(".easytabs-tabs > .easytabs-tab-item").forEach(tab => setTabActiveState(tab, false));
      const tab = correspondingTab(container, target);
      setTabActiveState(tab, true);
      const previous = tab?.previousElementSibling;
      if (previous) previous.classList.add("easytabs-before-active");
    }
  }

  function textFragments(clip) {
    const fragments = [];
    const walker = document.createTreeWalker(clip, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
      acceptNode(node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const style = getComputedStyle(node);
          if (node.matches("script, style, noscript, template, svg, [hidden]") || style.display === "none") {
            return NodeFilter.FILTER_REJECT;
          }
          return node.tagName === "BR" ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    let text = "";
    let previousBlock = null;
    let afterBreak = false;
    function append(character, node, offset) {
      if (/\s/u.test(character)) {
        if (!text || text.endsWith(" ")) return;
        character = " ";
      }
      text += character;
      fragments.push({ node, offset, end: text.length });
    }
    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        afterBreak = true;
        continue;
      }
      if (!node.nodeValue.trim()) {
        append(" ", node, node.nodeValue.length);
        continue;
      }
      let block = node.parentElement;
      while (block && block !== clip) {
        const display = getComputedStyle(block).display;
        if (!display.startsWith("inline") && display !== "contents") break;
        block = block.parentElement;
      }
      if (afterBreak || (previousBlock && block !== previousBlock)) append(" ", node, 0);
      let offset = 0;
      for (const character of node.nodeValue) {
        offset += character.length;
        append(character, node, offset);
      }
      previousBlock = block;
      afterBreak = false;
    }
    if (text.endsWith(" ")) {
      text = text.slice(0, -1);
      fragments.pop();
    }
    return { text, fragments };
  }

  function characterBoundary(clip) {
    const { text, fragments } = textFragments(clip);
    const fragment = fragments[Math.min(overviewPreviewLength, fragments.length) - 1];
    if (!fragment) return null;
    return {
      text,
      cutoff: fragment.end,
      previewText: text.slice(0, fragment.end),
      node: fragment.node,
      offset: fragment.offset
    };
  }

  function addToggleIcons(container) {
    container.querySelectorAll(".easytabs-accordion-item").forEach(button => {
      if (button.querySelector(".pdp-tab-toggle-icon")) return;
      const icon = document.createElement("span");
      icon.className = "pdp-tab-toggle-icon";
      icon.setAttribute("aria-hidden", "true");
      button.appendChild(icon);
    });
  }

  function setupIds(container) {
    const items = Array.from(container.querySelectorAll(itemSelector));
    const tabs = Array.from(container.querySelectorAll(".easytabs-tabs > .easytabs-tab-item"));
    let containerId = containerIds.get(container);
    if (!containerId) {
      containerId = ++containerSequence;
      containerIds.set(container, containerId);
    }
    const base = `pdp-info-${containerId}`;
    tabs.forEach((tab, index) => {
      const item = items[index];
      const accordion = item?.querySelector(".easytabs-accordion-item");
      const panel = item?.querySelector(".easytabs-content-holder");
      const anchor = tab.querySelector(":scope > a");
      setAttribute(tab, "id", `${base}-tab-${index}`);
      setAttribute(tab, "role", "tab");
      setAttribute(tab, "aria-controls", `${base}-panel-${index}`);
      if (anchor) {
        anchor.tabIndex = -1;
        setAttribute(anchor, "role", "none");
      }
      if (item) setAttribute(item, "id", `${base}-content-${index}`);
      if (accordion) {
        setAttribute(accordion, "id", `${base}-accordion-${index}`);
        setAttribute(accordion, "role", "button");
        setAttribute(accordion, "aria-controls", `${base}-panel-${index}`);
        const accordionAnchor = accordion.querySelector(":scope > a");
        if (accordionAnchor) {
          accordionAnchor.tabIndex = -1;
          setAttribute(accordionAnchor, "role", "none");
        }
      }
      if (panel) {
        setAttribute(panel, "id", `${base}-panel-${index}`);
        setAttribute(panel, "aria-labelledby", `${base}-tab-${index}`);
      }
    });
  }

  function initRoot(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const previews = new WeakMap();
    const containerStates = new WeakMap();
    let scheduled = false;
    let observer;

    function createPreview(item) {
      const holder = item.querySelector(".easytabs-content-holder");
      if (!holder) return null;
      const existing = previews.get(item);
      if (existing?.clip.parentElement === holder) return existing;
      if (existing) previews.delete(item);
      const clip = document.createElement("div");
      clip.className = "pdp-overview-clip";
      clip.id = `pdp-overview-clip-${++previewSequence}`;
      const summary = document.createElement("span");
      summary.className = "pdp-visually-hidden";
      summary.hidden = true;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "pdp-overview-read-more";
      button.hidden = true;
      const state = {
        clip,
        summary,
        button,
        expanded: false,
        truncatable: false,
        boundary: null,
        hiddenEntries: [],
        tailWrapper: null
      };
      button.addEventListener("click", () => {
        state.expanded = !state.expanded;
        applyPreview(state, item, root, true);
        observer?.takeRecords();
      }, options);
      holder.insertBefore(clip, holder.firstChild);
      Array.from(holder.childNodes).forEach(node => {
        if (node !== clip && node !== summary && node !== button) clip.appendChild(node);
      });
      button.setAttribute("aria-controls", clip.id);
      holder.append(summary, button);
      previews.set(item, state);
      return state;
    }

    function syncNewChildren(item, state) {
      const holder = item.querySelector(".easytabs-content-holder");
      if (!holder) return;
      Array.from(holder.childNodes).forEach(node => {
        if (node !== state.clip && node !== state.summary && node !== state.button) state.clip.appendChild(node);
      });
    }

    function restorePreviewContent(state) {
      const entries = state.hiddenEntries.slice().reverse();
      entries.forEach(entry => {
        const { node, created, hadHidden } = entry;
        if (!node.isConnected || !state.clip.contains(node)) return;
        if (created && node.parentNode) {
          const parent = node.parentNode;
          while (node.firstChild) parent.insertBefore(node.firstChild, node);
          node.remove();
          parent.normalize();
        } else if (!created) {
          if (hadHidden) node.setAttribute("hidden", "");
          else node.removeAttribute("hidden");
        }
      });
      state.hiddenEntries = [];
      state.tailWrapper = null;
    }

    function rememberHidden(node, created = false, hadHidden = false) {
      return { node, created, hadHidden };
    }

    function hideSibling(node, entries) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        entries.push(rememberHidden(node, false, node.hasAttribute("hidden")));
        node.setAttribute("hidden", "");
        return;
      }
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()) {
        const wrapper = document.createElement("span");
        node.parentNode.insertBefore(wrapper, node);
        wrapper.appendChild(node);
        wrapper.setAttribute("hidden", "");
        entries.push(rememberHidden(wrapper, true));
      }
    }

    function hidePreviewContent(state) {
      const { node, offset } = state.boundary;
      const parent = node.parentNode;
      const tail = node.splitText(offset);
      const tailWrapper = document.createElement("span");
      tailWrapper.className = "pdp-overview-tail";
      tailWrapper.setAttribute("hidden", "");
      parent.insertBefore(tailWrapper, tail);
      tailWrapper.appendChild(tail);
      const entries = [...state.hiddenEntries, rememberHidden(tailWrapper, true)];
      let cursor = tailWrapper;
      while (cursor && cursor !== state.clip) {
        let sibling = cursor.nextSibling;
        while (sibling) {
          hideSibling(sibling, entries);
          sibling = sibling.nextSibling;
        }
        cursor = cursor.parentElement;
      }
      state.hiddenEntries = entries;
      state.tailWrapper = tailWrapper;
    }

    function measurePreview(state, hideMedia) {
      restorePreviewContent(state);
      state.clip.classList.remove("is-preview-collapsed");
      state.clip.inert = false;
      state.clip.style.removeProperty("--pdp-preview-height");
      const media = Array.from(state.clip.querySelectorAll("img, picture, video, iframe, audio, canvas"))
        .filter(node => !node.closest("[hidden]"));
      if (hideMedia) {
        media.forEach(node => hideSibling(node, state.hiddenEntries));
      }
      const boundary = characterBoundary(state.clip);
      if (!boundary) return false;
      const clipTop = state.clip.getBoundingClientRect().top;
      const range = document.createRange();
      range.selectNodeContents(state.clip);
      range.setEnd(boundary.node, boundary.offset);
      const bottoms = Array.from(range.getClientRects())
        .filter(rect => rect.width > 0 && rect.height > 0)
        .map(rect => rect.bottom);
      const height = bottoms.length ? Math.max(...bottoms) - clipTop + 1 : 0;
      const remainingText = boundary.text.slice(boundary.cutoff).trim();
      if (height <= 0) return false;
      if (!remainingText && !media.length) return false;
      state.previewText = boundary.previewText;
      state.boundary = { node: boundary.node, offset: boundary.offset };
      state.clip.style.setProperty("--pdp-preview-height", `${Math.ceil(height)}px`);
      return true;
    }

    function clearPreviewState(state) {
      restorePreviewContent(state);
      state.truncatable = false;
      state.clip.classList.remove("is-preview-collapsed");
      state.clip.inert = false;
      state.clip.removeAttribute("aria-hidden");
      state.clip.style.removeProperty("--pdp-preview-height");
      state.button.hidden = true;
      state.button.removeAttribute("aria-expanded");
      state.summary.hidden = true;
      state.summary.textContent = "";
    }

    function applyPreview(state, item, scope, accordionMode) {
      const enabled = scope.dataset.pdpOverviewPreviewEnabled !== "false";
      if (!accordionMode || !enabled) {
        clearPreviewState(state);
        return;
      }
      syncNewChildren(item, state);
      state.truncatable = measurePreview(state, !state.expanded);
      state.button.hidden = !state.truncatable;
      if (!state.truncatable) {
        clearPreviewState(state);
        return;
      }
      const collapsed = !state.expanded;
      if (collapsed) hidePreviewContent(state);
      else restorePreviewContent(state);
      state.clip.classList.toggle("is-preview-collapsed", collapsed);
      state.clip.inert = collapsed;
      setBooleanAttribute(state.clip, "aria-hidden", collapsed);
      state.summary.textContent = state.previewText || "";
      state.summary.hidden = !collapsed;
      state.button.textContent = collapsed
        ? scope.dataset.pdpReadMore || "Read more"
        : scope.dataset.pdpReadLess || "Read less";
      setAttribute(state.button, "aria-expanded", collapsed ? "false" : "true");
    }

    function syncContainer(container) {
      const holder = container.querySelector(".easytabs-holder");
      if (!holder || !container.classList.contains("easytabs-inited")) return;
      let containerState = containerStates.get(container);
      if (!containerState) {
        containerState = { initialMobileApplied: false, wasAccordion: null };
        containerStates.set(container, containerState);
      }
      const tabList = container.querySelector(".easytabs-tabs");
      const accordionMode = holder.classList.contains("easytabs-accordion");
      const items = Array.from(container.querySelectorAll(itemSelector));
      const overview = overviewItem(container);
      const overviewTab = correspondingTab(container, overview);

      setupIds(container);
      addToggleIcons(container);
      setAttribute(tabList, "role", "tablist");
      setAttribute(tabList, "aria-orientation", "horizontal");
      setBooleanAttribute(tabList, "aria-hidden", accordionMode);
      tabList.inert = accordionMode;

      if (accordionMode !== containerState.wasAccordion) {
        const activeOverflowFocus = accordionMode
          ? tabList.contains(document.activeElement)
          : holder.contains(document.activeElement) && Boolean(document.activeElement.closest(".easytabs-accordion-item"));
        if (activeOverflowFocus) {
          const focusTarget = accordionMode
            ? container.querySelector(`${itemSelector}.easytabs-active .easytabs-accordion-item`)
            : container.querySelector(".easytabs-tab-item.easytabs-active");
          focusTarget?.focus({ preventScroll: true });
        }
        containerState.wasAccordion = accordionMode;
      }

      items.forEach((item, index) => {
        const tab = tabList?.querySelectorAll(":scope > .easytabs-tab-item")[index];
        const accordion = item.querySelector(".easytabs-accordion-item");
        const panel = item.querySelector(".easytabs-content-holder");
        const active = item.classList.contains("easytabs-active");
        setAttribute(tab, "role", "tab");
        setAttribute(tab, "aria-selected", active ? "true" : "false");
        tab.tabIndex = accordionMode ? -1 : active ? 0 : -1;
        setAttribute(accordion, "role", "button");
        setAttribute(accordion, "aria-expanded", active ? "true" : "false");
        accordion.tabIndex = accordionMode ? 0 : -1;
        setAttribute(panel, "aria-hidden", active ? "false" : "true");
        setAttribute(panel, "role", accordionMode ? "region" : "tabpanel");
        setAttribute(panel, "aria-labelledby", accordionMode
          ? accordion?.id || ""
          : tab?.id || "");
        panel.inert = !active;
      });

      if (accordionMode && !containerState.initialMobileApplied) {
        const previewEnabled = root.dataset.pdpOverviewPreviewEnabled !== "false";
        if (previewEnabled && overview) {
          activateOnly(container, overview, true);
          const state = createPreview(overview);
          if (state) state.expanded = false;
        } else {
          activateOnly(container, null, false);
        }
        containerState.initialMobileApplied = true;
      }

      if (overview) {
        const state = accordionMode
          ? createPreview(overview)
          : previews.get(overview);
        if (state) applyPreview(state, overview, root, accordionMode);
      }
    }

    function schedule() {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        if (!root.isConnected) {
          disposeRoot(root);
          return;
        }
        root.querySelectorAll(containerSelector).forEach(syncContainer);
        observer?.takeRecords();
      });
    }

    observer = new MutationObserver(records => {
      const hasExternalMutation = records.some(record => {
        const target = record.target instanceof Element ? record.target : record.target.parentElement;
        if (target?.closest(".pdp-overview-read-more, .pdp-visually-hidden")) return false;
        const clip = target?.closest(".pdp-overview-clip");
        if (!clip) return true;
        return record.type === "childList" && target === clip;
      });
      if (hasExternalMutation) schedule();
    });
    let observedWidth = root.getBoundingClientRect().width;
    const resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width === undefined || Math.abs(width - observedWidth) < 0.5) return;
      observedWidth = width;
      schedule();
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"]
    });
    resizeObserver.observe(root);
    root.addEventListener("click", schedule, options);
    instances.set(root, { abort, observer, resizeObserver });
    schedule();
  }

  function disposeRoot(root) {
    const instance = instances.get(root);
    if (!instance) return;
    instance.observer.disconnect();
    instance.resizeObserver.disconnect();
    instance.abort.abort();
    instances.delete(root);
  }

  function rootsWithin(node) {
    const roots = [];
    if (node instanceof Element && node.matches(rootSelector)) roots.push(node);
    if (node.querySelectorAll) roots.push(...node.querySelectorAll(rootSelector));
    return roots;
  }

  function initializeAll(node = document) {
    rootsWithin(node).forEach(initRoot);
  }

  document.addEventListener("DOMContentLoaded", () => initializeAll());
  document.addEventListener("easytabs_page_inited", () => initializeAll());
  document.addEventListener("shopify:section:load", event => initializeAll(event.target));
  document.addEventListener("shopify:section:unload", event => {
    rootsWithin(event.target).forEach(disposeRoot);
  });
  window.addEventListener("pageshow", () => initializeAll());
  if (document.readyState !== "loading") initializeAll();
})();
